// src/cockpit4/components/ProductDiagnosisPanel.tsx
// ============================================
// ② 产品诊断 - 输入产品名，诊断 AI 回答中的产品卡与正文提及表现
//
// 功能：输入「产品名（+ 品牌名可选）」，逐平台询问 6 大 AI，
//       分析该产品在 AI 回答中：
//         · 是否被提及（正文提及率 / 位次 / 引用片段）
//         · 是否以「产品卡」形式结构化呈现（标题 / 功能列表 / 价格 / 适用场景 / 优劣势）
//       输出产品卡得分（0-5）+ 综合诊断得分（0-100）+ 优化建议。
//
// 数据来源（真实，无 mock）：
//   · 走 GEODiagnosticRuntime（服务端代理 /{platform}-api 真实 API + DeepSeek 兜底）
//   · 产品卡特征检测基于真实 AI 回答文本规则解析
// ============================================

import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Package,
  Send,
  RefreshCw,
  BadgeCheck,
  BarChart3,
  Activity,
  Layers,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { DIAG_PLATFORMS, DIAG_PLATFORM_MAP } from "../../runtime/GrowthFactory/geoPlatforms";
import DiagPlatformSelector from "./DiagPlatformSelector";
import type {
  ProductDiagnosisResult,
  DiagPlatformKey,
} from "../../runtime/GrowthFactory/types";
import type { GEODiagnosticRuntime } from "../../runtime/GrowthFactory/GEODiagnosticRuntime";

type Stage = "idle" | "running" | "done" | "error";

/** 产品卡特征图标 */
const CARD_FEATURE_COLOR: Record<string, string> = {
  产品标题: "#22D3EE",
  功能列表: "#34D399",
  价格信息: "#FBBF24",
  适用场景: "#A78BFA",
  优劣势评价: "#F472B6",
};

export default function ProductDiagnosisPanel() {
  const [product, setProduct] = useState("酒店自助入住机");
  const [brand, setBrand] = useState("");
  const [selected, setSelected] = useState<DiagPlatformKey[]>(
    DIAG_PLATFORMS.map((p) => p.key)
  );
  const [stage, setStage] = useState<Stage>("idle");
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState<ProductDiagnosisResult | null>(null);
  const [error, setError] = useState("");
  const abortRef = useRef(false);

  const getRuntime = () =>
    RuntimeManager.getRuntime(RuntimeName.GEODiagnostic) as unknown as GEODiagnosticRuntime;

  const runDiagnosis = async () => {
    const p = product.trim();
    if (!p) {
      setError("请输入产品名称");
      setStage("error");
      return;
    }
    if (selected.length === 0) {
      setError("请至少勾选 1 个诊断平台");
      setStage("error");
      return;
    }
    abortRef.current = false;
    setError("");
    setResult(null);
    setStage("running");
    try {
      const data = await getRuntime().diagnoseProduct(
        p,
        brand.trim(),
        selected,
        (msg) => setProgress(msg)
      );
      if (abortRef.current) return;
      setResult(data);
      setStage("done");
    } catch (e: any) {
      if (abortRef.current) return;
      setError(e?.message || String(e));
      setStage("error");
    }
  };

  const scoreColor =
    (result?.diagnosisScore || 0) >= 70
      ? "#34D399"
      : (result?.diagnosisScore || 0) >= 45
      ? "#FBBF24"
      : "#F87171";

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 顶栏 */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Package size={16} className="text-violet-400" />
            <span className="text-[13px] font-semibold text-white tracking-tight">
              ② 产品诊断
            </span>
          </div>
          <p className="text-[10px] font-mono text-gray-500 mt-1">
            输入产品名 → 实时询问 6 大 AI → 分析产品卡（结构化特征）与正文提及表现
          </p>
        </div>
      </div>

      {/* 输入区 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2.5">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-mono text-gray-500">产品名称</label>
          <input
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="例如：酒店自助入住机"
            className="bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500/50"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-mono text-gray-500">品牌名（选填，用于关联诊断）</label>
          <div className="flex items-center gap-2">
            <input
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="例如：小舍科技（可留空）"
              className="flex-1 bg-black/20 border border-white/[0.08] rounded-md px-3 py-2 text-[12px] text-white placeholder:text-gray-600 focus:outline-none focus:border-violet-500/50"
            />
            <motion.button
              whileTap={{ scale: 0.96 }}
              onClick={runDiagnosis}
              disabled={stage === "running"}
              className="flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-medium
                         bg-violet-500/15 text-violet-300 border border-violet-500/30
                         hover:bg-violet-500/25 disabled:opacity-50 transition-colors"
            >
              {stage === "running" ? (
                <RefreshCw size={13} className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              {stage === "running" ? "诊断中…" : "开始诊断"}
            </motion.button>
          </div>
        </div>
        {error && <div className="text-[11px] text-red-400">{error}</div>}
      </div>

      {/* 诊断平台勾选区 */}
      <DiagPlatformSelector selected={selected} onChange={setSelected} />

      {progress && (
        <div className="text-[11px] font-mono text-cyan-300/80">
          <Activity size={12} className="inline mr-1" />
          <span className="text-cyan-400">▸</span> {progress}
        </div>
      )}

      {stage === "idle" && (
        <div className="text-[11px] text-gray-500 py-8 text-center border border-dashed border-white/[0.06] rounded-lg">
          输入产品名称，勾选要诊断的 AI 厂家，实时看这些 AI 如何介绍该产品——是否被提及、是否以结构化「产品卡」呈现、以及正文提及表现。
        </div>
      )}

      {result && (
        <>
          {/* 汇总看板 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight" style={{ color: scoreColor }}>
                {result.diagnosisScore}
                <small className="text-[11px] text-gray-500 font-mono">/100</small>
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">综合诊断得分</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">产品卡(50%) + 提及率(50%)</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-amber-400">
                {result.cardScore}
                <small className="text-[11px] text-gray-500 font-mono">/5</small>
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">产品卡得分</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">结构化特征命中数</div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight" style={{ color: "#34D399" }}>
                {Math.round(result.mentionRate * 100)}%
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">正文提及率</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.mentionCount}/{result.total} 个 AI 提到该产品
              </div>
            </div>
            <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
              <div className="text-[18px] font-bold tracking-tight text-white truncate">
                {result.product?.slice(0, 12) || "-"}
              </div>
              <div className="text-[11px] text-gray-300 mt-0.5">诊断产品</div>
              <div className="text-[9px] font-mono text-gray-600 mt-0.5">
                {result.checkedAt?.slice(11, 19) || ""} 执行
              </div>
            </div>
          </div>

          {/* 产品卡特征 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <Layers size={14} className="text-amber-400" />
              <span className="text-[12px] font-semibold text-white">产品卡结构化特征</span>
              <span className="text-[9px] font-mono text-gray-600">
                检测 AI 回答中的标题 / 功能列表 / 价格 / 适用场景 / 优劣势
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {result.cardHits.length === 0 ? (
                <span className="text-[11px] text-gray-500">
                  暂无产品卡结构化特征 —— AI 尚未以卡片形式介绍该产品。
                </span>
              ) : (
                result.cardHits.map((h) => (
                  <span
                    key={h}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium border"
                    style={{
                      color: CARD_FEATURE_COLOR[h] || "#CBD5E1",
                      borderColor: `${CARD_FEATURE_COLOR[h] || "#CBD5E1"}33`,
                      background: `${CARD_FEATURE_COLOR[h] || "#CBD5E1"}0d`,
                    }}
                  >
                    <CheckCircle2 size={11} />
                    {h}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* 诊断结论 */}
          <div
            className={`rounded-lg border p-3 flex items-start gap-2 ${
              result.diagnosisScore >= 70
                ? "bg-emerald-500/[0.06] border-emerald-500/20"
                : result.diagnosisScore >= 45
                ? "bg-amber-500/[0.06] border-amber-500/20"
                : "bg-red-500/[0.06] border-red-500/20"
            }`}
          >
            <BadgeCheck size={16} className="text-cyan-400 mt-0.5 shrink-0" />
            <div className="text-[12px] text-gray-200">
              <span className="font-semibold text-white">诊断结论：</span>
              {result.verdict}
            </div>
          </div>

          {/* 各平台明细 */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 size={14} className="text-violet-400" />
              <span className="text-[12px] font-semibold text-white">各 AI 产品提及明细</span>
              <span className="text-[9px] font-mono text-gray-600">提及 / 位次 / 引用片段</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {result.results.map((r) => {
                const p = DIAG_PLATFORM_MAP[r.platform as DiagPlatformKey];
                return (
                  <div
                    key={r.platform}
                    className={`rounded-md border p-2.5 ${
                      r.mentioned
                        ? "bg-emerald-500/[0.04] border-emerald-500/20"
                        : "bg-white/[0.01] border-white/[0.06]"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <span
                        className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold text-white"
                        style={{ background: p.color }}
                      >
                        {p.short}
                      </span>
                      <span className="text-[11px] font-medium text-white flex-1">{p.name}</span>
                      {r.mentioned ? (
                        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
                          <CheckCircle2 size={12} />
                          提及 {r.count} 次
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-[10px] text-gray-500">
                          <XCircle size={12} />
                          未提及
                        </span>
                      )}
                    </div>
                    {r.mentioned && (
                      <div className="text-[11px] text-gray-300 leading-relaxed bg-black/20 rounded p-2 border border-white/[0.04]">
                        “…{r.snippet}…”
                      </div>
                    )}
                    {r.error && <div className="text-[10px] text-red-400/80">{r.error}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
